// src/components/logistique/parametrage/ListesParc.jsx
// ============================================================================
// Paramétrage de la logistique - les catégories et les lieux du parc
// ============================================================================
//
// Les listes que proposent les fiches d'actif. Jusqu'ici, elles ne se créaient
// que depuis une fiche ; ici, on les renomme, on les désactive ou réactive, on
// range les catégories. Désactiver ne touche aucun actif : il garde sa
// catégorie ou son lieu, qui n'est plus proposé aux nouveaux. Rien ne se
// supprime. Écrire demande LOGISTIQUE.PARC.MANAGE ; sans elle, on lit.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Check, ListTree, Plus, X } from 'lucide-react';
import PorteurNotification from '../../ui/PorteurNotification';
import { Button } from '../../ui/button';
import { BOUTON_CONTOUR } from '../../ui/boutons';
import EnTeteSection from '../../parametrage/EnTeteSection';
import CarteReglages from '../../parametrage/CarteReglages';
import ChargementSection from '../../parametrage/ChargementSection';
import { Interrupteur } from '../../parametrage/PiecesReglage';
import { parc } from '../apiLogistique';
import { BandeauEchec } from '../elementsLogistique';

const ACCENT = '#319190';
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-[#319190] focus:ring-2 focus:ring-[#319190]/15';
const PETIT = `${BOUTON_CONTOUR} w-8 px-0`;

// `enCours` : la clé de l'action en cours (« categorie-3 », « lieu-nouveau ») :
// seule sa ligne attend, les autres restent telles quelles.
const Liste = ({ titre, quoi, elements, gerer, enCours, onRenommer, onBasculer, onDeplacer, onAjouter }) => {
  const occupe = (e) => enCours === `${quoi}-${e.id}`;
  const [edition, setEdition] = useState(null);
  const [nouveau, setNouveau] = useState('');
  const actifs = elements.filter((e) => e.actif);
  return (
    <CarteReglages titre={titre} accent={ACCENT} compteur={`${actifs.length} proposé${actifs.length > 1 ? 's' : ''}`} dense>
      {elements.map((e) => {
        const rang = actifs.findIndex((a) => a.id === e.id);
        return (
          <div key={e.id} className={`flex flex-wrap items-center gap-3 px-5 py-3 ${e.actif ? 'bg-white' : 'bg-gray-50/40'} hover:bg-gray-50/70`}>
            <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: e.actif ? ACCENT : '#9ca3af' }} />
            {edition?.id === e.id ? (
              <form
                className="flex min-w-[220px] flex-1 items-center gap-2"
                onSubmit={(ev) => { ev.preventDefault(); onRenommer(e, edition.nom).then((ok) => { if (ok) setEdition(null); }); }}
              >
                <input aria-label={`Nouveau nom de ${e.nom}`} className={CHAMP} maxLength={80} value={edition.nom} onChange={(ev) => setEdition({ id: e.id, nom: ev.target.value })} autoFocus />
                <Button type="submit" size="sm" className={PETIT} aria-label="Enregistrer le nom" disabled={occupe(e)}><Check className="h-4 w-4" /></Button>
                <Button type="button" size="sm" className={PETIT} aria-label="Annuler" onClick={() => setEdition(null)}><X className="h-4 w-4" /></Button>
              </form>
            ) : (
              <div className="min-w-[200px] flex-1">
                <p className={`text-sm font-semibold ${e.actif ? 'text-gray-800' : 'text-gray-400'}`}>{e.nom}</p>
                <p className="text-[11px] text-gray-400">{e.actifs} actif{e.actifs > 1 ? 's' : ''} en service{e.actif ? '' : ' - plus proposé'}</p>
              </div>
            )}
            {gerer && edition?.id !== e.id && (
              <div className="flex items-center gap-1.5">
                {onDeplacer && e.actif && (
                  <>
                    <Button type="button" size="sm" className={PETIT} aria-label={`Monter ${e.nom}`} disabled={Boolean(enCours) || rang <= 0} onClick={() => onDeplacer(e, 'haut')}><ArrowUp className="h-4 w-4" /></Button>
                    <Button type="button" size="sm" className={PETIT} aria-label={`Descendre ${e.nom}`} disabled={Boolean(enCours) || rang >= actifs.length - 1} onClick={() => onDeplacer(e, 'bas')}><ArrowDown className="h-4 w-4" /></Button>
                  </>
                )}
                <Button type="button" size="sm" className={`${BOUTON_CONTOUR} w-[100px]`} disabled={occupe(e)} onClick={() => setEdition({ id: e.id, nom: e.nom })}>Renommer</Button>
              </div>
            )}
            <Interrupteur actif={e.actif} accent={ACCENT} libelle={`${e.nom} proposé${quoi === 'categorie' ? 'e' : ''}`} desactive={!gerer} enCours={occupe(e)} onBasculer={() => onBasculer(e)} />
          </div>
        );
      })}
      {gerer && (
        <form
          className="flex flex-wrap items-center gap-2 px-5 py-3"
          onSubmit={(ev) => { ev.preventDefault(); if (nouveau.trim().length >= 2) onAjouter(nouveau.trim()).then((ok) => { if (ok) setNouveau(''); }); }}
        >
          <input aria-label={quoi === 'lieu' ? 'Nouveau lieu' : 'Nouvelle catégorie'} className={`${CHAMP} max-w-sm`} maxLength={80} value={nouveau} onChange={(ev) => setNouveau(ev.target.value)} placeholder={quoi === 'lieu' ? 'Salle 12, Bureau du directeur...' : 'Instruments de musique...'} />
          <Button type="submit" size="sm" disabled={enCours === `${quoi}-nouveau` || nouveau.trim().length < 2} className="gap-2 bg-[#319190] text-white shadow-sm hover:bg-[#2a7a79]">
            <Plus className="h-4 w-4" /> Ajouter
          </Button>
        </form>
      )}
    </CarteReglages>
  );
};

const ListesParc = () => {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      setDonnees(await parc.parametrage());
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (erreur && !donnees) {
    return (
      <BandeauEchec onReessayer={charger}>{erreur}</BandeauEchec>
    );
  }
  if (!donnees) return <ChargementSection accent={ACCENT} libelle="Chargement des catégories et des lieux…" />;
  const gerer = Boolean(donnees.droits?.gerer);

  const agir = async (cle, appel, message) => {
    setEnCours(cle);
    try {
      await appel();
      setToast({ message, type: 'success' });
      await charger();
      return true;
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
      return false;
    } finally {
      setEnCours(null);
    }
  };

  const pour = (quoi) => ({
    quoi,
    gerer,
    enCours,
    onRenommer: (e, nom) => agir(`${quoi}-${e.id}`, () => (quoi === 'lieu' ? parc.modifierLieu(e.id, { nom }) : parc.modifierCategorie(e.id, { nom })), 'Nom enregistré.'),
    onBasculer: (e) => agir(`${quoi}-${e.id}`, () => (quoi === 'lieu' ? parc.modifierLieu(e.id, { actif: !e.actif }) : parc.modifierCategorie(e.id, { actif: !e.actif })),
      e.actif ? `« ${e.nom} » n'est plus proposé.` : `« ${e.nom} » est de nouveau proposé.`),
    onAjouter: (nom) => agir(`${quoi}-nouveau`, () => (quoi === 'lieu' ? parc.creerLieu(nom) : parc.creerCategorie(nom)), `« ${nom} » ajouté.`)
  });

  return (
    <div className="space-y-5">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={ListTree}
        titre="Catégories et lieux"
        sousTitre={gerer ? 'Les listes proposées dans les fiches d\'actif. Désactiver ne touche aucun actif.' : 'Lecture seule : il faut la permission de gérer le parc pour changer ces listes'}
        accent={ACCENT}
        accentSombre="#2a7a79"
      />
      <Liste titre="Catégories" elements={donnees.categories} {...pour('categorie')} onDeplacer={(e, sens) => agir(`categorie-${e.id}`, () => parc.deplacerCategorie(e.id, sens), 'Ordre enregistré.')} />
      <Liste titre="Lieux" elements={donnees.lieux} {...pour('lieu')} />
    </div>
  );
};

export default ListesParc;
